import {
  matchPath,
  useParams,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  Grid,
  GridCellProps,
  GridNoRecords,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridCustomRowProps
} from '@progress/kendo-react-grid';
import { Button } from 'react-bootstrap';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { useSessionStorage } from 'usehooks-ts';
import { process, State } from '@progress/kendo-data-query';
import React, { useContext, useEffect, useState } from 'react';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import { Error } from '@progress/kendo-react-labels';
import { ERR_INPUT_REQUIRED } from '../../../../constants/text';

import Toolbar from '../../toolbar';
import { CardView } from './card-view';
import {
  AddResourceAsync,
  AddZoneAsync,
  DeleteZoneAsync,
  UpdateGameDocState,
  UpdateZoneAsync,
  UpdateResourceAsync,
  ExportZones,
  ImportZones,
  GetResourceValue
} from '../../../../utils/game-document';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../../../../components/grid/grid-tool-bar';
import { uuid } from '../../../../types/common-helper';
import { getCurrentTimeStamp } from '../../../../utils/date';
import { useToast } from '../../../../contexts/toast-context';
import { ColumnMenu } from '../../../../components/columnMenu';
import { PostResourceAsync } from '../../../../services/files';
import { useBreadcrumb } from '../../../../contexts/breadcrumb';
import { AddZoneLibraryAsync } from '../../../../services/zone';
import { NoRecords } from '../../../../components/grid/no-records';
import YesNoDialog from '../../../../components/dialogs/yes-no-dialog';
import { GameDocumentContext } from '../../../../contexts/game-document';
import { downloadJSON, handleFileUpload } from '../../../../utils/files';
import { ZoneResponse } from '../../../../types/responses/zone-response';
import { DefaultGridSettings } from '../../../../constants/grid-settings';
import { ZoneEditorWindow } from '../../../../features/game-document/zones';
import { EntityEditor } from '../../../../types/game-document/entity-editor';
import { ResourceEntity, ZoneEntity } from '../../../../types/game-document/';
import { generateZoneSnapshot } from '../../../../utils/generate-zone-snapshot';
import { ActionButtonsCell } from '../../../../components/grid/action-buttons-cell';
import { ExportedZoneData } from '../../../../types/game-document/entities/exported-zone';

const initialDataState: State = {
  sort: [{ field: 'title', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const Zones = () => {
  const [state, setState] = useContext(GameDocumentContext);
  const [dataState, setDataState] = useSessionStorage<State>(
    'datastate-zones',
    initialDataState
  );
  const [resourceEditorValue, setResourceEditorValue] =
    useState<EntityEditor<ResourceEntity>[]>();
  const [entityEditorValue, setEntityEditorValue] =
    useState<EntityEditor<ZoneEntity>>();
  const [entityEditorIsVisible, setEntityEditorIsVisible] =
    useState<boolean>(false);
  const [showConfirmationDelete, setShowConfirmationDelete] = useState('');
  const [gridMode, setGridMode] = useState<'grid' | 'card'>('card');
  const { onToastOpen } = useToast();

  const [copyZoneModalVisible, setCopyZoneModalVisible] = useState<
    string | null
  >(null);
  const [copyZoneTitle, setCopyZoneTitle] = useState('');
  const [copyZoneTitleError, setCopyZoneTitleError] = useState<string>('');
  const [copyZoneDescription, setCopyZoneDescription] = useState('');
  const [isCopying, setIsCopying] = useState<boolean>(false);

  const toggleEntityEditor = () => {
    setEntityEditorIsVisible(!entityEditorIsVisible);
  };

  const navigate = useNavigate();
  const { gameId } = useParams();

  const titleGuid = uuid();

  const zones = React.useMemo(() => {
    const entities =
      state.gameDocument?.assets?.zones
        ?.filter((x) => x.id !== '-')
        .map((zone) => ({
          ...zone,
          title: GetResourceValue(state.gameDocument!, zone.titleResId ?? '')
        })) ?? [];

    return process(entities, dataState);
  }, [state.gameDocument, dataState]);

  useEffect(() => {
    // Set the page zone.
    document.title = `Zone Maps - ${state.gameDocument?.name}`;
  }, [state]);

  const CustomRow = (props: GridCustomRowProps) => (
    <tr {...props.trProps} style={{ cursor: 'pointer' }}>
      {props.children}
    </tr>
  );

  const handleSingleZoneExport = (zoneId: string) => {
    const exportedZone = ExportZones(state.gameDocument!, [zoneId]);
    const zone = state.gameDocument?.assets?.zones?.find(
      (z) => z.id === zoneId
    );
    const zoneName =
      GetResourceValue(state.gameDocument!, zone?.titleResId ?? '') ?? 'zone';
    const formattedTimestamp = getCurrentTimeStamp();
    downloadJSON(
      exportedZone,
      `zone-map-${zoneName}-${formattedTimestamp}.json`
    );
  };

  const ActionButtonsCellWrapper = (props: GridCellProps) => (
    <ActionButtonsCell
      {...props}
      actions={[
        {
          onClick: (e) => {
            e.stopPropagation();
            onViewEntity(props.dataItem['id']);
          },
          icon: 'edit',
          themeColor: 'primary',
          title: 'Edit'
        },
        {
          onClick: (e) => {
            e.stopPropagation();
            handleSingleZoneExport(props.dataItem['id']);
          },
          icon: 'download_for_offline',
          themeColor: 'info',
          title: 'Export to JSON'
        },
        {
          onClick: (e) => {
            e.stopPropagation();
            const zone = state.gameDocument?.assets?.zones?.find(
              (z) => z.id === props.dataItem['id']
            );
            const title =
              GetResourceValue(state.gameDocument!, zone?.titleResId ?? '') ||
              '';
            setCopyZoneTitle(title);
            setCopyZoneTitleError('');
            setCopyZoneDescription(zone?.description ?? '');
            setCopyZoneModalVisible(props.dataItem['id']);
          },
          icon: 'library_add_check',
          themeColor: 'primary',
          title: 'Copy Zone Map to My Zone Map Library'
        },
        {
          onClick: (e) => {
            e.stopPropagation();
            setShowConfirmationDelete(props.dataItem['id']);
          },
          icon: 'delete',
          themeColor: 'error',
          title: 'Delete'
        }
      ]}
    />
  );

  const handleEntityEditorSubmit = (
    editorEntity: EntityEditor<ZoneEntity>,
    resourceEntity: EntityEditor<ResourceEntity>[]
  ) => {
    if (editorEntity.isNew) {
      const addZoneResource = AddResourceAsync(
        state.gameDocument!,
        'Title',
        '',
        'text',
        resourceEntity.find(
          (item) => item.entity.id === editorEntity.entity.titleResId!
        )?.entity.value!,
        titleGuid
      );
      Promise.all([addZoneResource]).then(() => {
        const {
          entity: { name, description }
        } = editorEntity;

        AddZoneAsync(state.gameDocument!, name, description, titleGuid).then(
          (updatedGameDocument) => {
            setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
            setEntityEditorIsVisible(false);
          }
        );
      });
    } else {
      const zoneResource = resourceEntity.find(
        (resource) => resource.entity.id === editorEntity.entity.titleResId
      );

      const updateZoneResource = UpdateResourceAsync(
        state.gameDocument!,
        editorEntity.entity.titleResId!,
        zoneResource?.entity!
      );

      Promise.all([updateZoneResource]).then(() => {
        UpdateZoneAsync(
          state.gameDocument!,
          editorEntity.entity.id,
          editorEntity.entity
        ).then((updatedGameDocument) => {
          setState((prev) => UpdateGameDocState(prev, updatedGameDocument));

          setEntityEditorIsVisible(false);
        });
      });
    }
  };

  const onViewEntity = (entityId: string) => {
    navigate(
      `/designer/${gameId}/map/${entityId !== undefined ? entityId : ''}`
    );
  };

  const handleJsonImport = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleFileUpload<ExportedZoneData>(
      event,
      async (exportedZone) => {
        ImportZones(state.gameDocument!, exportedZone).then(
          (updatedGameDocument) => {
            setState((state) => UpdateGameDocState(state, updatedGameDocument));
            onToastOpen({
              title: 'Info',
              body: 'Import Success',
              style: 'info'
            });
          }
        );
      },
      (message) => {
        onToastOpen({
          title: 'Error',
          body: message,
          style: 'error'
        });
      }
    );
  };

  const onDeleteEntity = async (entityId: string) => {
    DeleteZoneAsync(state.gameDocument!, entityId).then(
      (updatedGameDocument) => {
        setState((prev) => UpdateGameDocState(prev, updatedGameDocument));
        setShowConfirmationDelete('');
      }
    );
  };

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const location = useLocation();
  const { setBreadcrumb } = useBreadcrumb();

  const handleCopyZoneSubmit = async () => {
    if (!copyZoneModalVisible || isCopying) return;

    if (!copyZoneTitle || copyZoneTitle.trim() === '') {
      setCopyZoneTitleError(ERR_INPUT_REQUIRED);
      return;
    }

    setIsCopying(true);
    try {
      const exportedZone = ExportZones(state.gameDocument!, [
        copyZoneModalVisible
      ]);

      const zoneBoundary = exportedZone.zones[0].boundary;
      let previewUrl = '';
      if (exportedZone.maps[0].type === 'illustrated') {
        previewUrl = GetResourceValue(
          state.gameDocument!,
          exportedZone.maps[0].imageResId ?? ''
        );
      } else {
        const blob = await generateZoneSnapshot(zoneBoundary);
        const zoneName =
          copyZoneTitle ||
          GetResourceValue(
            state.gameDocument!,
            exportedZone.zones[0]?.titleResId ?? ''
          ) ||
          'zone';
        const formattedTimestamp = getCurrentTimeStamp();
        const fileName = `zone-${zoneName}-preview-url-${formattedTimestamp}.jpeg`;

        const extractedBlob: File = new File([blob], fileName, {
          type: blob.type
        });

        // upload the file to azure blob
        const respUpload = await PostResourceAsync(extractedBlob);
        previewUrl = respUpload.value;
      }

      const payload: ZoneResponse = {
        name: copyZoneTitle.trim(),
        previewImageUrl: previewUrl,
        description: copyZoneDescription,
        gameSourceId: gameId as string,
        content: exportedZone
      };
      await AddZoneLibraryAsync(payload);
      onToastOpen({
        title: 'Success',
        body: 'Zone copied to library',
        style: 'success'
      });
      setCopyZoneModalVisible(null);
      setCopyZoneTitleError('');
    } catch (error) {
      onToastOpen({
        title: 'Error',
        body: 'Failed to copy zone map to library',
        style: 'error'
      });
    } finally {
      setIsCopying(false);
    }
  };

  useEffect(() => {
    const breadcrumbPaths = [
      { path: '/designer/:gameId/assets/*', text: 'Assets' },
      { path: '/designer/:gameId/assets/zones/*', text: 'Zone maps' }
    ];

    const newBreadcrumb = [];
    for (const config of breadcrumbPaths) {
      const match = matchPath(config.path, location.pathname);

      if (match) {
        newBreadcrumb.push({ text: config.text, to: match.pathnameBase });
      }
    }
    setBreadcrumb(newBreadcrumb);

    return () => {
      setBreadcrumb([]);
    };
  }, [setBreadcrumb, location.pathname]);

  return (
    <>
      <Toolbar
        title={'Zone maps'}
        buttonHelpSupport={{
          title: 'Zone map assets guide',
          url: 'https://forum.catalystglobal.com/t/4635'
        }}>
        {entityEditorIsVisible && (
          <ZoneEditorWindow
            toggleDialog={toggleEntityEditor}
            onSubmit={handleEntityEditorSubmit}
            onClose={toggleEntityEditor}
            editorResource={resourceEditorValue!}
            editorEntity={entityEditorValue!}
            editorMode={'basic'}
          />
        )}
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search zone maps'}
          columnsToSearch={['title', 'description']}
          showJsonImport={true}
          handleJsonImport={handleJsonImport}
          defaultGridMode={'card'}
          exportRef={gridExportRef}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode as 'grid' | 'card');
          }}
        />
        <ExcelExport
          data={(state.gameDocument?.assets.zones ?? []).map((zone) => ({
            ...zone,
            titleResId: GetResourceValue(
              state.gameDocument!,
              zone.titleResId ?? ''
            )
          }))}
          ref={gridExportRef}
          fileName={`${state.gameDocument?.name} zone maps.xlsx`}>
          <ExcelExportColumn field={'titleResId'} title={'Title'} />
          <ExcelExportColumn field={'description'} title={'Description'} />
        </ExcelExport>

        {gridMode === 'grid' && (
          <Grid
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            className={'k-grid--with-5-actions'}
            dataItemKey={'id'}
            data={zones}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
            onRowClick={(event) => onViewEntity(event.dataItem['id'])}
            rows={{
              data: CustomRow
            }}>
            <Column field={'title'} title={'Title'} columnMenu={ColumnMenu} />
            <Column
              field={'description'}
              title={'Description'}
              columnMenu={ColumnMenu}
            />
            <Column cells={{ data: ActionButtonsCellWrapper }} />
            <GridNoRecords>
              <NoRecords />
            </GridNoRecords>
          </Grid>
        )}

        {gridMode === 'card' && (
          <CardView
            onDelete={(id) => setShowConfirmationDelete(id)}
            onExport={(id) => handleSingleZoneExport(id)}
            onCopy={(id) => {
              const zone = state.gameDocument?.assets?.zones?.find(
                (z) => z.id === id
              );
              const title =
                GetResourceValue(state.gameDocument!, zone?.titleResId ?? '') ||
                '';
              setCopyZoneTitle(title);
              setCopyZoneTitleError('');
              setCopyZoneDescription(zone?.description ?? '');
              setCopyZoneModalVisible(id);
            }}
            data={zones.data}
            total={zones.data.length}
          />
        )}
      </div>

      {showConfirmationDelete && (
        <YesNoDialog
          title={'Confirm removal'}
          onConfirm={(action) =>
            action === 'yes'
              ? onDeleteEntity(showConfirmationDelete)
              : setShowConfirmationDelete('')
          }
          onClose={() => setShowConfirmationDelete('')}>
          Are you sure you want to remove the zone map?
        </YesNoDialog>
      )}

      {copyZoneModalVisible && (
        <Window
          title={'Copy Zone Map to My Zone Map Library'}
          onClose={() => {
            if (isCopying) return;
            setCopyZoneModalVisible(null);
            setCopyZoneTitleError('');
          }}
          initialWidth={450}
          initialHeight={350}>
          <div className="mb-3">
            <label className="k-label">Title</label>
            <Input
              value={copyZoneTitle}
              onChange={(e) => {
                setCopyZoneTitle(e.value);
                if (copyZoneTitleError && e.value && e.value.trim() !== '') {
                  setCopyZoneTitleError('');
                }
              }}
              disabled={isCopying}
              valid={copyZoneTitleError ? false : undefined}
              style={{ width: '100%' }}
            />
            {copyZoneTitleError && <Error>{copyZoneTitleError}</Error>}
          </div>
          <div className="mb-3">
            <label className="k-label">Description</label>
            <TextArea
              value={copyZoneDescription}
              onChange={(e) => setCopyZoneDescription(e.value)}
              disabled={isCopying}
              style={{ width: '100%' }}
            />
          </div>
          <WindowActionsBar>
            <Button
              className={'btn-secondary text-light'}
              disabled={isCopying}
              onClick={() => {
                setCopyZoneModalVisible(null);
                setCopyZoneTitleError('');
              }}>
              Cancel
            </Button>
            <Button
              className={'text-light'}
              color={'primary'}
              disabled={isCopying}
              onClick={handleCopyZoneSubmit}>
              {isCopying ? 'Copying...' : 'Copy'}
            </Button>
          </WindowActionsBar>
        </Window>
      )}
    </>
  );
};

export default Zones;
